import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SiteHeader } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { listMyContests } from "@/lib/authApi";
import useAuth from "@/hooks/useAuth";
import {
  Trophy,
  ArrowRight,
  ArrowLeft,
  Crown,
  Lock,
  Loader2,
  DollarSign,
} from "lucide-react";

function contestStatus(c) {
  if (c.status === "final") return "final";
  if (c.status === "locked") return "locked";
  if (c.lock_at && new Date(c.lock_at) <= new Date()) return "locked";
  return "open";
}

export default function MyContests() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [contests, setContests] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    listMyContests()
      .then(setContests)
      .catch(() => setError("Couldn't load your contests. Try again in a minute."));
  }, []);

  return (
    <div className="min-h-screen bg-pbo-paper">
      <SiteHeader />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-xs text-pbo-muted hover:text-pbo-green mb-6"
          data-testid="my-contests-back-link"
        >
          <ArrowLeft className="w-3 h-3" />
          Back to home
        </Link>

        <div className="flex items-center gap-3 mb-1">
          <span className="w-10 h-10 rounded-full bg-pbo-gold/15 text-pbo-gold grid place-items-center">
            <Trophy className="w-5 h-5" />
          </span>
          <h1 className="font-serif text-pbo-green text-3xl sm:text-4xl">
            My contests
          </h1>
        </div>
        {user && (
          <p className="text-pbo-muted text-sm mb-8">
            Every contest linked to <strong className="text-pbo-green">{user.email}</strong>
          </p>
        )}

        {!contests && !error && (
          <div className="flex items-center gap-2 text-pbo-muted text-sm py-10 justify-center" data-testid="my-contests-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}

        {error && (
          <div className="bg-white border border-red-200 rounded-2xl p-6 text-sm text-red-700" data-testid="my-contests-error">
            {error}
          </div>
        )}

        {contests && contests.length === 0 && (
          <div className="bg-white border border-pbo-border rounded-2xl p-8 text-center" data-testid="my-contests-empty">
            <DollarSign className="w-10 h-10 text-pbo-gold mx-auto mb-3" />
            <h2 className="font-serif text-pbo-green text-xl mb-2">
              No contests yet
            </h2>
            <p className="text-pbo-ink/70 text-sm mb-6">
              Host a salary-cap contest or join one with a code — it&apos;ll show
              up here automatically.
            </p>
            <div className="flex gap-3 justify-center flex-wrap">
              <Button
                onClick={() => nav("/contest/new")}
                className="bg-pbo-gold text-pbo-ink hover:bg-pbo-gold-soft rounded-full font-semibold"
                data-testid="my-contests-host-btn"
              >
                Host a contest
              </Button>
              <Button
                variant="outline"
                onClick={() => nav("/contest/join")}
                className="border-pbo-green text-pbo-green hover:bg-pbo-green hover:text-pbo-cream rounded-full"
                data-testid="my-contests-join-btn"
              >
                Join with a code
              </Button>
            </div>
          </div>
        )}

        {contests && contests.length > 0 && (
          <div className="space-y-3" data-testid="my-contests-list">
            {contests.map((c) => {
              const status = contestStatus(c);
              return (
                <button
                  key={c.code}
                  onClick={() => nav(`/contest/${c.code}`)}
                  className="w-full text-left bg-white border border-pbo-border rounded-2xl p-5 hover:border-pbo-gold hover:shadow-[0_16px_36px_-20px_rgba(15,77,42,0.4)] transition-all group"
                  data-testid={`my-contest-${c.code}`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-serif text-pbo-green text-lg truncate">
                          {c.name || c.code}
                        </span>
                        {c.is_host && (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold bg-pbo-gold/15 text-pbo-gold px-2 py-0.5 rounded-full">
                            <Crown className="w-3 h-3" /> Host
                          </span>
                        )}
                        <span
                          className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${
                            status === "final"
                              ? "bg-pbo-gold/20 text-pbo-gold"
                              : status === "locked"
                              ? "bg-pbo-green/10 text-pbo-green"
                              : "bg-emerald-50 text-emerald-700"
                          }`}
                        >
                          {status === "final" && <Trophy className="w-3 h-3" />}
                          {status === "locked" && <Lock className="w-3 h-3" />}
                          {status === "final"
                            ? "Final"
                            : status === "locked"
                            ? "Locked"
                            : "Open"}
                        </span>
                      </div>
                      <div className="text-xs text-pbo-muted mt-1.5 flex items-center gap-3 flex-wrap">
                        <span className="font-mono tracking-[0.2em] text-pbo-green">
                          {c.code}
                        </span>
                        <span>Playing as {c.username}</span>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-pbo-muted group-hover:text-pbo-gold flex-shrink-0 transition-colors" />
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
